// vg-binding: CONTEXT:decisions            (P15.D-20 rewire-no-new-ui, P15.D-07 audience-scoped, P15.D-21 fail-closed UX + signal-only WS + optimistic)
// vg-binding: CONTEXT:P15.D-18             (Task 29 — signal-only notification.new; legacy notification.create RETIRED, G-35)
// vg-binding: CONTEXT:P15.D-08             (audit-zero DoD — 0 FE `.on('notification.create')`)
// vg-binding: INTERFACE-STANDARDS:error-shape
// apps/vendor/src/components/notifications/NotificationCenter.tsx
//
// Phase 15 (Notification & Email Consolidation) — Task 24
// Vendor TopBar bell re-wired onto the CANONICAL notification surface. The old
// dead `/api/v1/vendor/notifications` data path is replaced by the audience-
// scoped canonical hooks (features/notifications/canonical.ts). The audience is
// derived server-side from the portal JWT — the vendor bell now SHOWS vendor
// notifications and never leaks merchant rows (TS-03 / TS-06).
//
// Wiring (P15.D-20 — rewire only, no new page/design):
//   - list feed  → GET /api/v1/notifications          (useNotificationFeed)
//   - badge      → GET /api/v1/notifications/unread-count (useUnreadCount)
//   - mark-read  → PATCH /api/v1/notifications/:id/read   (optimistic + rollback)
//   - read-all   → POST  /api/v1/notifications/read-all   (optimistic + rollback)
//
// Fail-closed UX (P15.D-21):
//   - 403 AUDIENCE_FORBIDDEN → banner + retry (NOT an empty state).
//   - authorized + zero rows → real empty state.
//   - WS is SIGNAL-ONLY → any notification.* event refetches the feed +
//     unread-count (never self-increments/self-prepends → no double-count).
//   - mark-read / read-all are optimistic with onError rollback + toast.
//
// title/body come pre-rendered at the caller locale from the BE (P15.D-14) — the
// FE displays them verbatim (no client-side re-render from i18n_key).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button, useToast } from '@printway/ui';
import type { WsClientHandle } from '@printway/ui';

import {
  NotificationItem,
  type NotificationItemData,
} from './NotificationItem.js';
import {
  isAudienceForbidden,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationFeed,
  useNotificationRefetch,
  useUnreadCount,
  type CanonicalNotification,
} from '../../features/notifications/canonical.js';
import { isNotificationBundleStale } from '../../features/notifications/wsVersionFloor.js';

// Vendor notification tab filters (UI unchanged from Phase 7 — P15.D-20).
type FilterKey = 'all' | 'orders' | 'products' | 'payments' | 'system';

const FILTER_KEYS: ReadonlyArray<FilterKey> = [
  'all',
  'orders',
  'products',
  'payments',
  'system',
];

/**
 * Canonical `type` is `event_type ?? category` (a dotted event name or a bare
 * category). Derive the tab bucket best-effort so the existing filter tabs keep
 * working. The default `all` tab always shows the full audience-scoped feed.
 */
function deriveFilter(type: string): FilterKey {
  const t = type.toLowerCase();
  if (t === 'orders' || t === 'products' || t === 'payments' || t === 'system') {
    return t as FilterKey;
  }
  if (t.includes('order')) return 'orders';
  if (t.includes('product') || t.includes('stock') || t.includes('inventory')) {
    return 'products';
  }
  if (
    t.includes('pay') ||
    t.includes('earning') ||
    t.includes('payout') ||
    t.includes('wallet') ||
    t.includes('invoice')
  ) {
    return 'payments';
  }
  return 'system';
}

/** Map a canonical row → the presentational item shape (title/body verbatim). */
function toItemData(n: CanonicalNotification): NotificationItemData {
  return {
    id: n.id,
    title: n.title,
    body: n.body,
    category: n.type,
    isRead: n.read,
    createdAt: n.createdAt,
    link: n.link ?? undefined,
    // Server already rendered title/body at the caller locale (D-14) — do NOT
    // re-render from i18n_key on the FE.
    i18nKey: null,
    i18nParams: null,
  };
}

export interface NotificationCenterProps {
  /** Live WS client handle. When null the dropdown is REST/poll-only. */
  wsClient?: WsClientHandle | null;
}

export function NotificationCenter({
  wsClient = null,
}: NotificationCenterProps): JSX.Element {
  const { t } = useTranslation('orders');
  const { toast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<FilterKey>('all');
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Badge is driven by the scoped unread-count endpoint (P15.D-21 — per-portal
  // count), independent of whether the panel/feed has been opened.
  const unreadQuery = useUnreadCount();
  const unread =
    unreadQuery.isSuccess && typeof unreadQuery.data === 'number'
      ? unreadQuery.data
      : 0;

  // Feed only fetches while the panel is open — a closed bell never polls.
  const feedQuery = useNotificationFeed({ limit: 20, enabled: open });
  const refetchNotifications = useNotificationRefetch();
  const markReadM = useMarkNotificationRead();
  const markAllM = useMarkAllNotificationsRead();

  const items = useMemo<NotificationItemData[]>(
    () => (feedQuery.data?.items ?? []).map(toItemData),
    [feedQuery.data],
  );

  const feedForbidden = isAudienceForbidden(feedQuery.error);
  // A generic (non-audience) error still deserves the inline error line.
  const feedGenericError = feedQuery.isError && !feedForbidden;

  const visible = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter((n) => deriveFilter(n.category) === filter);
  }, [items, filter]);

  // -------------------------------------------------------------------------
  // WS: SIGNAL-ONLY refetch (P15.D-21). Any notification.* event triggers a
  // refetch of the feed + unread-count — never a local prepend/increment.
  //
  // P15.D-18: the canonical publisher event is `notification.new` (signal-only).
  // The legacy `notification.create` dual-listen was REMOVED in Task 29 with the
  // server-side emitter (G-35) — 0 `.on('notification.create')` remains. Each
  // frame also runs the version-floor check (P15.D-18) so an old bundle is
  // prompted to reload. membership.revoked/granted (P15.D-07) bust the badge +
  // refetch; the refetch's HTTP status decides the view (403 banner / 200 empty
  // / rows).
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!wsClient) return;
    const events = [
      'notification.new',
      'notification.read',
      'notification.read_all',
      'membership.revoked',
      'membership.granted',
    ];
    const offs = events.map((evt) =>
      wsClient.on(evt, (ev) => {
        if (isNotificationBundleStale(ev.payload)) {
          toast({
            variant: 'default',
            title: t('notifications.reloadPrompt'),
          });
        }
        refetchNotifications();
      }),
    );
    return () => {
      for (const off of offs) off();
    };
  }, [wsClient, refetchNotifications, toast, t]);

  // -------------------------------------------------------------------------
  // Click-outside / Escape / coordinate with @printway/ui TopBar dropdowns
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    const onDoc = (ev: MouseEvent): void => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(ev.target as Node)) setOpen(false);
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') setOpen(false);
    };
    const onOtherDropdown = (ev: Event): void => {
      const detail = (ev as CustomEvent<{ id: string }>).detail;
      if (detail && detail.id !== 'vendor-notification-center') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('topbar:dropdown:open', onOtherDropdown);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('topbar:dropdown:open', onOtherDropdown);
    };
  }, [open]);

  // -------------------------------------------------------------------------
  // Mutations — optimistic in the react-query cache; component surfaces toasts.
  // -------------------------------------------------------------------------
  const handleMarkRead = useCallback(
    (id: string): void => {
      markReadM.mutate(id, {
        onError: () => {
          toast({
            variant: 'error',
            title: t('notifications.toast.markReadFailed'),
          });
        },
      });
    },
    [markReadM, toast, t],
  );

  const handleMarkAll = useCallback((): void => {
    markAllM.mutate(undefined, {
      onError: () => {
        toast({
          variant: 'error',
          title: t('notifications.toast.markAllFailed'),
        });
      },
    });
  }, [markAllM, toast, t]);

  // Retry after a 403 AUDIENCE_FORBIDDEN — re-runs BOTH the feed AND the
  // unread-count (the badge). The old handler refetched only the feed, leaving a
  // stale badge count next to a freshly-cleared banner.
  const handleForbiddenRetry = useCallback((): void => {
    void feedQuery.refetch();
    void unreadQuery.refetch();
  }, [feedQuery, unreadQuery]);

  const toggleOpen = useCallback((): void => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        window.dispatchEvent(
          new CustomEvent('topbar:dropdown:open', {
            detail: { id: 'vendor-notification-center' },
          }),
        );
      }
      return next;
    });
  }, []);

  const feedLoading = feedQuery.isLoading || feedQuery.isFetching;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label={t('notifications.centerAria')}
        aria-expanded={open}
        onClick={toggleOpen}
        data-testid="vendor-notif-bell-btn"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-pw-text transition-colors hover:bg-pw-surface focus:outline-none focus:ring-2 focus:ring-pw-primary-strong/40"
      >
        {/* Bell icon — inline SVG to avoid icon-lib dep churn. */}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          className="h-5 w-5"
          aria-hidden="true"
        >
          <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14V11a6 6 0 1 0-12 0v3a2 2 0 0 1-.6 1.4L4 17h5" />
          <path d="M9 17a3 3 0 0 0 6 0" />
        </svg>
        {unread > 0 ? (
          <span
            data-testid="notification-badge-count"
            className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-pw-primary-strong px-1 text-[10px] font-semibold leading-4 text-white"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={t('notifications.centerAria')}
          data-testid="vendor-notif-center"
          className="absolute right-0 z-50 mt-2 w-96 max-w-[calc(100vw-1rem)] overflow-hidden rounded-lg border border-pw-border bg-pw-bg shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b border-pw-border px-4 py-2">
            <span className="text-sm font-semibold text-pw-text">
              {t('notifications.panelTitle')}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                data-testid="vendor-notif-markallread-btn"
                onClick={handleMarkAll}
                disabled={markAllM.isPending || unread === 0}
              >
                {t('notifications.page.markAllRead')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void feedQuery.refetch()}
                disabled={feedLoading}
              >
                {feedLoading
                  ? t('notifications.panelLoading')
                  : t('notifications.panelRefresh')}
              </Button>
            </div>
          </div>

          {/* Filter tabs (UI unchanged — P15.D-20). */}
          <div className="flex border-b border-pw-border px-2">
            {FILTER_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                data-testid={`notification-filter-${key}`}
                className={[
                  'border-b-2 px-3 py-2 text-xs font-medium transition-colors',
                  filter === key
                    ? 'border-pw-primary-strong text-pw-primary-strong'
                    : 'border-transparent text-pw-text-muted hover:text-pw-text',
                ].join(' ')}
              >
                {t(`notifications.filter.${key}`)}
              </button>
            ))}
          </div>

          {/* List / states */}
          <div className="max-h-96 overflow-y-auto">
            {feedForbidden ? (
              // 2-case revoke (P15.D-21): audience-block → banner + retry, NOT
              // an empty state. 403-semantic (post-review FA): 403
              // AUDIENCE_FORBIDDEN is now reserved for a genuine no-identity
              // token; an org-revoked vendor gets 200 with a personal feed
              // instead (flows through membership.revoked → refetch). Testids
              // aligned to the cross-portal `notif-forbidden-banner` /
              // `notif-retry-btn` convention (merchant + admin).
              <div
                role="alert"
                data-testid="notif-forbidden-banner"
                className="flex flex-col items-center gap-2 px-4 py-8 text-center"
              >
                <p className="text-sm font-medium text-pw-text">
                  {t('notifications.revoke.title')}
                </p>
                <p className="text-xs text-pw-text-muted">
                  {t('notifications.revoke.body')}
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="notif-retry-btn"
                  onClick={handleForbiddenRetry}
                  disabled={feedLoading}
                >
                  {t('notifications.revoke.retry')}
                </Button>
              </div>
            ) : feedGenericError ? (
              <p className="px-4 py-6 text-center text-sm text-red-600">
                {t('notifications.errorLoad', {
                  code: (feedQuery.error as Error)?.message ?? 'ERROR',
                })}
              </p>
            ) : visible.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-pw-text-muted">
                {feedLoading
                  ? t('notifications.panelLoading')
                  : t('notifications.emptyList')}
              </p>
            ) : (
              visible.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onRead={handleMarkRead}
                  onActivate={(item) => {
                    const link = item.link;
                    if (typeof link === 'string' && link.length > 0) {
                      setOpen(false);
                      const target = link.includes('?focus=')
                        ? link
                        : `${link}${link.includes('?') ? '&' : '?'}focus=${encodeURIComponent(item.id)}`;
                      navigate(target);
                    }
                  }}
                />
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
